import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, Search } from "lucide-react";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/9aa213a6-9b93-496d-b454-969c7d67f960/images/80b5831e8eb35a7d67db619b6b98959a8312a899cbdbc39b43bf61ef5e0fcc19.png";
const HERO = "https://images.pexels.com/photos/31725399/pexels-photo-31725399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = params.get("next");
      if (next && next.startsWith("/")) navigate(next);
      else navigate(u.role === "engineer" ? "/engineer" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 grid-bg">
        <div className="max-w-md w-full mx-auto fade-up">
          <div className="flex items-center gap-3 mb-12">
            <img src={LOGO} alt="RoSo" className="h-11 w-11 object-contain" />
            <div>
              <div className="font-heading font-black text-xl leading-none">RoSo Multi Trading</div>
              <div className="label-caps text-muted-foreground mt-1">Service Desk</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-2">
            Staff sign in
          </h1>
          <p className="text-muted-foreground mb-8">
            Manage complaints, engineers and SLAs in one place.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="label-caps">Login ID</Label>
              <Input
                id="email" type="text" value={email} required
                data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="Enter your login ID"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input
                id="password" type="password" value={password} required
                data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="bg-signal/10 border border-signal text-signal text-sm px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-sm bg-klein hover:bg-klein/90 text-white font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} className="ml-1" />
            </Button>
          </form>

          <div className="mt-8 border border-border bg-white p-4 text-sm">
            <div className="label-caps text-muted-foreground mb-2">Authorized staff access only</div>
            <p className="text-muted-foreground">
              Use the Login ID and password provided by your administrator.
            </p>
          </div>

          <Link to="/track" className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-klein" data-testid="public-track-link">
            <Search size={15} /> Track a complaint (customer)
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="hidden lg:block relative bg-ink">
        <img src={HERO} alt="Technician servicing appliance" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <div className="label-caps text-warn mb-3">Air Conditioners · Coolers · Refrigerators · Washing Machines</div>
          <h2 className="font-heading text-4xl font-black tracking-tight max-w-md">
            Every complaint tracked. Every SLA met.
          </h2>
        </div>
      </div>
    </div>
  );
}
